import React, { useContext, useEffect, useState } from 'react';
import AutoCompleteField from '../../core/components/Fields/AutoCompleteField';
import TextField from '../../core/components/Fields/TextField';
import { useAssistanceFieldContext } from '../pages/Assistance/AssistanceFieldContext';
import StringField from '../../core/components/Fields/StringField';
import { useTranslation } from 'react-i18next';
import useConfirmValueUpdate from './useConfirmValueUpdate';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import { renderClientOption } from './useLookup';
import AssistanceSection from '../pages/Assistance/AssistanceSection';
import {
    IHeaderDataFieldGroupProps,
    useHeaderDataFieldGroupProps,
} from '../pages/Assistance/customizable/HeaderDataFieldGroup';
import { FieldProps, useFieldProps } from '../pages/Assistance/customizable/Field';
import { IConditionalComponent } from '../../customizations/ConditionalComponentContext';
import useForceSelectionLookupProps, { useForceSelectionReselectEventListener } from './useForceSelectionLookupProps';
import { HeaderDataFieldGroupControls } from '../pages/Assistance/HeaderDataSection';
import { useAssistanceViewContext } from '../pages/Assistance/AssistanceViewContext';
import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig';

const joinAddress = (address) => {
    return (
        [
            address.name || '',
            address.companyName || '',
            address.streetAndNr || '',
            address.misc || '',
            `${address.postcode || ''} ${address.city || ''}`.trim(),
            address.country || '',
        ]
            .filter((i) => i)
            .join('\n') || ''
    );
};

interface IConfirmValueOptions {
    onConfirm: () => void;
    onCancel: () => void;
    waitingForConfirm: boolean;
}

const ConfirmValueContext = React.createContext<{
    confirmValueOptions: IConfirmValueOptions;
    onConfirmValueOptionsChange: (options: IConfirmValueOptions) => void;
}>({
    confirmValueOptions: {
        onConfirm: () => {},
        onCancel: () => {},
        waitingForConfirm: false,
    },
    onConfirmValueOptionsChange: (options: IConfirmValueOptions) => {},
});

export const CustomerFieldGroup = ({ children, fieldName, fields, controls, ...props }: IHeaderDataFieldGroupProps) => {
    const { t } = useTranslation('assistance');

    const [confirmValueOptions, setConfirmValueOptions] = useState({
        onConfirm: () => {},
        onCancel: () => {},
        waitingForConfirm: false,
    });

    const { onFieldReselect } = useAssistanceViewContext();

    const resetConfirmValueOptions = () => {
        setConfirmValueOptions({
            onConfirm: () => {},
            onCancel: () => {},
            waitingForConfirm: false,
        });
    };

    const handleFieldReselect = ({ fieldName, isEmailOcr, bbox, pageIndex }) => {
        setConfirmValueOptions({
            onConfirm: () => {
                resetConfirmValueOptions();
                return onFieldReselect({ fieldName, isEmailOcr, bbox, pageIndex });
            },
            onCancel: () => {
                resetConfirmValueOptions();
            },
            waitingForConfirm: true,
        });
    };

    const fieldGroupProps = useHeaderDataFieldGroupProps({ fieldName, fields, children, controls });
    const controlsProps = (React.Children.toArray(controls)[0] as any)?.props || {}; // reuse props from slot
    return (
        <ConfirmValueContext.Provider
            value={{ confirmValueOptions, onConfirmValueOptionsChange: setConfirmValueOptions }}
        >
            <AssistanceSection.FieldGroup
                {...fieldGroupProps}
                title={props.title || fieldGroupProps.title}
                controls={
                    <HeaderDataFieldGroupControls {...controlsProps} onFieldReselect={handleFieldReselect} hideClear />
                }
            >
                {children}

                <ConfirmModal
                    onConfirm={confirmValueOptions.onConfirm}
                    onCancel={confirmValueOptions.onCancel}
                    visible={confirmValueOptions.waitingForConfirm}
                >
                    <p>{t('fields.customerField.modal')}</p>
                </ConfirmModal>
            </AssistanceSection.FieldGroup>
        </ConfirmValueContext.Provider>
    );
};

export const CustomerNumberConfirmField = (props: FieldProps) => {
    const { clientPartitionId, config } = useAssistanceFieldContext();
    const { lookupDefinitionId, lookupFieldName, allowInvalidIds, performLookups } = config;

    const fieldProps = useFieldProps(props);
    const { value, onValueChange, onFocus, onBlur, onCommit } = fieldProps;

    const { onConfirmValueOptionsChange } = useContext(ConfirmValueContext);
    const confirmValueOptions = useConfirmValueUpdate({
        value,
        onValueChange,
        onUpdatePayloadChange: props.onUpdatePayloadChange,
        onFocus,
        onBlur,
        onCommit,
    });

    useEffect(() => {
        onConfirmValueOptionsChange(confirmValueOptions);
    }, [confirmValueOptions.waitingForConfirm]);

    const forceSelectionLookupProps = useForceSelectionLookupProps({
        lookupType: 'ClientLookupType',
        lookupDefinitionId,
        lookupFieldName,
        partitionId: clientPartitionId,
        allowInvalidIds,
        value,
        onValueChange,
        onUpdatePayloadChange: props.onUpdatePayloadChange,
        onFocus: confirmValueOptions.onFocus,
        onBlur: confirmValueOptions.onBlur,
        onCommit: confirmValueOptions.onCommit,
        isPartitionIdField: true,
    });

    useForceSelectionReselectEventListener(props.inputRef, forceSelectionLookupProps.onValueChange);

    // If lookups are not enabled for this field, render it as a plain StringField
    if (!performLookups || !lookupDefinitionId || !lookupFieldName) {
        return <StringField {...fieldProps} />;
    }

    return <AutoCompleteField {...fieldProps} {...forceSelectionLookupProps} renderOption={renderClientOption} />;
};

export const CustomerNumberField = (props: FieldProps) => {
    const { clientPartitionId, config } = useAssistanceFieldContext();
    const { lookupDefinitionId, lookupFieldName, allowInvalidIds, performLookups } = config;

    const fieldProps = useFieldProps(props);
    const { value, onValueChange, onFocus, onBlur, onCommit } = fieldProps;

    const forceSelectionLookupProps = useForceSelectionLookupProps({
        lookupType: 'ClientLookupType',
        lookupDefinitionId,
        lookupFieldName,
        partitionId: clientPartitionId,
        allowInvalidIds,
        value,
        onValueChange,
        onUpdatePayloadChange: props.onUpdatePayloadChange,
        onFocus,
        onBlur,
        onCommit,
        isPartitionIdField: true,
    });

    useForceSelectionReselectEventListener(props.inputRef, forceSelectionLookupProps.onValueChange);

    // If lookups are not enabled for this field, render it as a plain StringField
    if (!performLookups || !lookupDefinitionId || !lookupFieldName) {
        return <StringField {...fieldProps} />;
    }

    return <AutoCompleteField {...fieldProps} {...forceSelectionLookupProps} renderOption={renderClientOption} />;
};

// Note: the read-only display fields below must never commit — especially CustomerAddressField,
// whose displayed value is a derived string (joinAddress) that must not be written back
const CustomerAddressField = (props: FieldProps) => {
    const { value, ...fieldProps } = useFieldProps(props);

    return (
        <TextField
            {...fieldProps}
            onCommit={undefined}
            readOnly={true}
            disabled={true}
            value={value && joinAddress(value)}
        />
    );
};

const CustomerInternalNoteField = (props: FieldProps) => {
    const fieldProps = useFieldProps(props);

    return <TextField {...fieldProps} onCommit={undefined} readOnly={true} disabled={true} />;
};

const CustomerRelatedInfoField = (props: FieldProps) => {
    const fieldProps = useFieldProps(props);

    return <StringField {...fieldProps} onCommit={undefined} readOnly={true} disabled={true} />;
};

export const getCustomerFieldConditionalComponents = (customerFieldKey: string): IConditionalComponent[] => [
    {
        // line item field
        condition: ({ fieldName, itemIndex }) =>
            itemIndex !== undefined && fieldName === `${customerFieldKey}${GROUP_SEPARATOR}customer_number`,
        component: CustomerNumberField,
    },
    {
        // header field
        condition: ({ fieldName, itemIndex }) =>
            itemIndex === undefined && fieldName === `${customerFieldKey}${GROUP_SEPARATOR}customer_number`,
        component: CustomerNumberConfirmField,
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}customer_number2`,
        component: CustomerRelatedInfoField,
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}name`,
        component: CustomerNumberConfirmField, // We can use the same component for both fields
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}address`,
        component: CustomerAddressField,
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}internal_note`,
        component: CustomerInternalNoteField,
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}customer_type`,
        component: CustomerRelatedInfoField,
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}iln`,
        component: CustomerRelatedInfoField,
    },
    {
        condition: ({ fieldName }) => fieldName === `${customerFieldKey}${GROUP_SEPARATOR}contact_person`,
        component: CustomerRelatedInfoField,
    },
];

export const getCustomerFieldGroupConditionalComponents = (customerFieldKey: string) => [
    {
        condition: ({ fieldName }) => fieldName === customerFieldKey,
        component: CustomerFieldGroup,
    },
];
